import { PDFDocument, PDFFont } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { rgb } from 'pdf-lib';
import { addNcodeLayerToAllPages } from "../../../ndp/NdpPdfPrinter";
import { IPageSOBP } from "../../../neolab-libs/nl-lib3-common";
import { IProjectSubmissionStudentNameTransfer } from "../../../repositories/model/transfer/IProjectSubmissionStudentNameTransfer";


// SampleQuestionGenerator.ts

// Grid specification with versioning
export type GridSpec = {
    version: string;
    page: { width: number; height: number };
    grid: {
        rows: number;
        cols: number;
        margins: { left: number; right: number; top: number; bottom: number };
        gaps: { col: number; row: number };
    };
};

export const USB_GRID_V1: GridSpec = {
    version: 'usb-grid/v1',
    page: { width: 595, height: 842 },
    grid: {
        rows: 10,
        cols: 4,
        margins: { left: 40, right: 40, top: 100, bottom: 40 },
        gaps: { col: 10, row: 10 },
    },
};

export type GridHit = { index: number; row: number; col: number } | null;

export class USBAssignedUsersPDFGenerator {

	// Instance function to generate rubric
	async generatePDF(studentsList: IProjectSubmissionStudentNameTransfer[], spec: GridSpec = USB_GRID_V1, startSOBP: IPageSOBP, projectCode: string, projectName: string, curriculumYear: string)  {
		
		// 1) Create the target (new) PDF
		const newDoc = await PDFDocument.create();
		newDoc.registerFontkit(fontkit);

		// 2) Embed fonts in the target doc (fonts must be embedded in the doc you draw into)
		let fontBytes = await fetch('/font/Pretendard-Light.ttf').then(res => res.arrayBuffer());
		const lightFont = await newDoc.embedFont(fontBytes);
		fontBytes = await fetch('/font/NotoSansKR-Thin.ttf').then(res => res.arrayBuffer());
		const dashFont = await newDoc.embedFont(fontBytes);
		
		fontBytes = await fetch('/font/Pretendard-Medium.ttf').then(res => res.arrayBuffer());
		const font = await newDoc.embedFont(fontBytes);
		fontBytes = await fetch('/font/Pretendard-SemiBold.ttf').then(res => res.arrayBuffer());
		const boldFont = await newDoc.embedFont(fontBytes);

		// 3) Load the source PDF
		const srcBytes = await fetch('/pdf/userlistBG.pdf').then(r => r.arrayBuffer());
		const srcDoc = await PDFDocument.load(srcBytes);

		const page = newDoc.getPage(0);
		const pageWidth = spec.page.width;
		const pageHeight = spec.page.height;

		// 4) Print student names
		const cols = spec.grid.cols;
		const rows = spec.grid.rows;
		const capacityPerPage = rows * cols;
		const totalStudents = studentsList?.length || 0;
		const totalPagesRequired = Math.max(1, Math.ceil(totalStudents / capacityPerPage));

		const marginLeft = spec.grid.margins.left;
		const marginRight = spec.grid.margins.right;
		const marginTop = spec.grid.margins.top; // below title
		const marginBottom = spec.grid.margins.bottom;
		const colGap = spec.grid.gaps.col;
		const rowGap = spec.grid.gaps.row;

		const availableWidth = pageWidth - marginLeft - marginRight - (cols - 1) * colGap;
		const availableHeight = pageHeight - marginTop - marginBottom - (rows - 1) * rowGap;
		const cellWidth = availableWidth / cols;
		const cellHeight = availableHeight / rows;


		// Helper to ellipsize text to fit within a cell
		const ellipsize = (text: string, maxWidth: number, font: PDFFont, fontSize: number) => {
			if (!text) return '';
			let display = text;
			const ellipsis = '…';
			while (font.widthOfTextAtSize(display, fontSize) > maxWidth && display.length > 0) {
				display = display.slice(0, -1);
			}
			if (display !== text && display.length > 0) {
				// Ensure ellipsis still fits
				while (font.widthOfTextAtSize(display + ellipsis, fontSize) > maxWidth && display.length > 0) {
					display = display.slice(0, -1);
				}
				display += ellipsis;
			}
			return display;
		};

		for (let p = 0; p < totalPagesRequired; p++) {

			// add page
			const [templatePage] = await newDoc.copyPages(srcDoc, [0]);
			const page = newDoc.addPage(templatePage);

			// Title per page
			const title = 'USB 연결 학생 번호표';
			const titleSize = 18;
			const titleWidth = boldFont.widthOfTextAtSize(title, titleSize);
			const titleX = (pageWidth - titleWidth) / 2;
			const titleY = pageHeight - 60;
			/*
			page.drawText(title, {
				x: titleX,
				y: titleY,
				size: titleSize,
				font: PretendardBoldKR,
				color: rgb(0, 0, 0)
			});
			*/

			const examName = projectName;
			const examCode = projectCode;
			if (examName && examCode) {

				const examNameX = marginLeft + 1 * (cellWidth + colGap);
				const examNameY = pageHeight - 58;

				page.drawText(examName, {
							x: examNameX,
							y: examNameY,
							size: 17,
							font: font,
							color: rgb(0, 0, 0)
						});

				const examCodeX = marginLeft + 3 * (cellWidth + colGap) + cellWidth;
				const examTextWidth = lightFont.widthOfTextAtSize(examCode, 9);
				

				page.drawText(examCode, {
							x: examCodeX - examTextWidth,
							y: examNameY + 2,
							size: 9,
							font: lightFont,
							color: rgb(0, 0, 0)
						});
				
			}

			

			// Cells per page
			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {

					// 1 draw cell rectangle if possible
					const globalIdx = p * capacityPerPage + (r * cols + c);
					const x = marginLeft + c * (cellWidth + colGap);
					const y = pageHeight - marginTop - (r + 1) * cellHeight - r * rowGap;
/*
					// Draw cell boundary (rectangle)
					page.drawRectangle({
						x,
						y,
						width: cellWidth,
						height: cellHeight,
						borderWidth: 1,
						borderColor: rgb(0, 0, 0)
					});
*/
					// 2 draw bluetooth name 'AIGLE-001'
					const blNum = (globalIdx + 1).toString().padStart(3, '0');
					const blName = `AIGLE ${blNum}`;
					if (blName) {
						const maxTextWidth = cellWidth - 12;
						const displayName = ellipsize(blName, maxTextWidth, lightFont, 9);
						const textWidth = lightFont.widthOfTextAtSize(displayName, 9);
						const textX = x + (cellWidth - textWidth) / 2;
						const textY = y + 45; // slight vertical tweak
						page.drawText(blName, {
							x: textX,
							y: textY,
							size: 9,
							font: lightFont,
							color: rgb(0, 0, 0)
						});

						// 2-1 draw dash in the middle of 'AIGLE' and '001'
						const dash = " -";
						page.drawText(dash, {
							x: textX + 21,
							y: textY + 0.5,
							size: 10,
							font: dashFont,
							color: rgb(0, 0, 0)
						});
					}

					// 3 draw school name
					const scName = curriculumYear + ' ' + studentsList[globalIdx]?.school;
					if (scName && studentsList[globalIdx]) {
						const maxTextWidth = cellWidth - 12;
						const displayName = ellipsize(scName, maxTextWidth, lightFont, 9);
						const textWidth = lightFont.widthOfTextAtSize(displayName, 9);
						const textX = x + (cellWidth - textWidth) / 2;
						const textY = y + 24; // slight vertical tweak
						page.drawText(scName, {
							x: textX,
							y: textY,
							size: 9,
							font: lightFont,
							color: rgb(0, 0, 0)
						});
					}

					// 4 draw name
					const name = `${studentsList[globalIdx]?.grade ?? ''}학년 ${studentsList[globalIdx]?.className ?? ''}반 ${studentsList[globalIdx]?.number ?? ''}번 ${studentsList[globalIdx]?.name ?? ''}`.slice(0, 17);
					
					if (name && studentsList[globalIdx]) {
						const maxTextWidth = cellWidth - 12;
						const displayName = ellipsize(name, maxTextWidth, font, 9);
						const textWidth = lightFont.widthOfTextAtSize(displayName, 9);
						const textX = x + (cellWidth - textWidth) / 2;
						const textY = y + 11;
						page.drawText(displayName, {
							x: textX,
							y: textY,
							size: 9,
							font: boldFont,
							color: rgb(0, 0, 0)
						});
					}
				}
			}
		}

		// 5) Add NCode
		const pageCount = newDoc.getPageCount();
		const basePdfBytes = await newDoc.save();
		const baseBlob = new Blob([basePdfBytes], { type: 'application/pdf' });
		const basePdfURL = URL.createObjectURL(baseBlob);

		const ndpBytes = await addNcodeLayerToAllPages({
			fileUrl: basePdfURL,
			ndpPdf: {
				section: startSOBP.section,
				owner: startSOBP.owner,
				bookCode: startSOBP.book,
				pageStart: startSOBP.page,
				pageEnd: startSOBP.page + pageCount - 1,
			}
		});

		const finalBlob = new Blob([ndpBytes], { type: 'application/pdf' });
		return { pdfBlobURL: URL.createObjectURL(finalBlob), pageCount };

		/*
		// 1) Create A4-size PDF (approx 595 x 842 points)
		const pdfDoc = await PDFDocument.create();
		pdfDoc.registerFontkit(fontkit);

		// Embed fonts similar to usage in NcodeAllocationStore
		let fontBytes = await fetch('/font/Pretendard-Light.ttf').then(res => res.arrayBuffer());
		const PretendardKR = await pdfDoc.embedFont(fontBytes);
		fontBytes = await fetch('/font/Pretendard-SemiBold.ttf').then(res => res.arrayBuffer());
		const PretendardBoldKR = await pdfDoc.embedFont(fontBytes);

		const pageWidth = spec.page.width;
		const pageHeight = spec.page.height;

		// 2) Pagination setup and drawing across pages
		const cols = spec.grid.cols;
		const rows = spec.grid.rows;
		const capacityPerPage = rows * cols;
		const totalStudents = studentsList?.length || 0;
		const totalPages = Math.max(1, Math.ceil(totalStudents / capacityPerPage));

		const marginLeft = spec.grid.margins.left;
		const marginRight = spec.grid.margins.right;
		const marginTop = spec.grid.margins.top; // below title
		const marginBottom = spec.grid.margins.bottom;
		const colGap = spec.grid.gaps.col;
		const rowGap = spec.grid.gaps.row;

		const availableWidth = pageWidth - marginLeft - marginRight - (cols - 1) * colGap;
		const availableHeight = pageHeight - marginTop - marginBottom - (rows - 1) * rowGap;
		const cellWidth = availableWidth / cols;
		const cellHeight = availableHeight / rows;

		const nameFont = PretendardKR;
		const nameSize = 12;

		// Helper to ellipsize text to fit within a cell
		const ellipsize = (text: string, maxWidth: number) => {
			if (!text) return '';
			let display = text;
			const ellipsis = '…';
			while (nameFont.widthOfTextAtSize(display, nameSize) > maxWidth && display.length > 0) {
				display = display.slice(0, -1);
			}
			if (display !== text && display.length > 0) {
				// Ensure ellipsis still fits
				while (nameFont.widthOfTextAtSize(display + ellipsis, nameSize) > maxWidth && display.length > 0) {
					display = display.slice(0, -1);
				}
				display += ellipsis;
			}
			return display;
		};

		for (let p = 0; p < totalPages; p++) {
			const page = pdfDoc.addPage([pageWidth, pageHeight]);

			// Title per page
			const title = 'USB 연결 학생 번호표';
			const titleSize = 18;
			const titleWidth = PretendardBoldKR.widthOfTextAtSize(title, titleSize);
			const titleX = (pageWidth - titleWidth) / 2;
			const titleY = pageHeight - 60;
			page.drawText(title, {
				x: titleX,
				y: titleY,
				size: titleSize,
				font: PretendardBoldKR,
				color: rgb(0, 0, 0)
			});

			// Cells per page
			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {
					const globalIdx = p * capacityPerPage + (r * cols + c);
					const x = marginLeft + c * (cellWidth + colGap);
					const y = pageHeight - marginTop - (r + 1) * cellHeight - r * rowGap;

					// Draw cell boundary (rectangle)
					page.drawRectangle({
						x,
						y,
						width: cellWidth,
						height: cellHeight,
						borderWidth: 1,
						borderColor: rgb(0, 0, 0)
					});

					const name = studentsList[globalIdx]?.name || '';
					if (name) {
						const maxTextWidth = cellWidth - 12;
						const displayName = ellipsize(name, maxTextWidth);
						const textWidth = nameFont.widthOfTextAtSize(displayName, nameSize);
						const textX = x + (cellWidth - textWidth) / 2;
						const textY = y + (cellHeight - nameSize) / 2 + 2; // slight vertical tweak
						page.drawText(displayName, {
							x: textX,
							y: textY,
							size: nameSize,
							font: nameFont,
							color: rgb(0, 0, 0)
						});
					}
				}
			}
		}

		// Add NCode
		const pageCount = pdfDoc.getPageCount();
		const basePdfBytes = await pdfDoc.save();
		const baseBlob = new Blob([basePdfBytes], { type: 'application/pdf' });
		const basePdfURL = URL.createObjectURL(baseBlob);

		const ndpBytes = await addNcodeLayerToAllPages({
			fileUrl: basePdfURL,
			ndpPdf: {
				section: startSOBP.section,
				owner: startSOBP.owner,
				bookCode: startSOBP.book,
				pageStart: startSOBP.page,
				pageEnd: startSOBP.page + pageCount - 1,
			}
		});

		const finalBlob = new Blob([ndpBytes], { type: 'application/pdf' });
		return { pdfBlobURL: URL.createObjectURL(finalBlob), pageCount };
		*/
	}
	

}